import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCcw, Clock, CreditCard, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const Refund = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] to-[#D1FAE5]">
      {/* Header */}
      <div className="bg-[#064E3B] text-white py-6">
        <div className="container mx-auto px-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white hover:bg-white/10 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          <h1 className="text-3xl md:text-4xl font-bold">Refund Policy</h1>
          <p className="text-gray-300 mt-2">Last updated: October 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8 md:p-12">
          
          <div className="flex items-center gap-3 mb-8">
            <RefreshCcw className="h-8 w-8 text-[#064E3B]" />
            <h2 className="text-2xl font-bold text-[#064E3B]">Our Refund Policy</h2>
          </div>

          <div className="bg-[#D1FAE5] border border-[#84CC16] rounded-lg p-4 mb-8">
            <p className="text-[#064E3B] font-medium">
              We want you to be completely satisfied with MoneyFeed. If you're not happy with your Pro subscription, we offer a hassle-free refund policy.
            </p>
          </div>

          <div className="space-y-8 text-gray-700">
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Clock className="h-5 w-5 text-[#84CC16]" />
                <h3 className="text-xl font-semibold text-[#064E3B]">1. 14-Day Money-Back Guarantee</h3>
              </div>
              <p className="mb-3">
                If you're not satisfied with your Pro subscription, you can request a full refund within 14 days of your initial purchase. No questions asked.
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Applies to first-time Pro subscriptions only</li>
                <li>Request must be made within 14 days of purchase</li>
                <li>Full refund will be processed within 5-7 business days</li>
              </ul>
            </section>

            <section>
              <div className="flex items-center gap-2 mb-3">
                <CreditCard className="h-5 w-5 text-[#84CC16]" />
                <h3 className="text-xl font-semibold text-[#064E3B]">2. Subscription Cancellation</h3>
              </div>
              <p className="mb-3">
                You can cancel your Pro subscription at any time:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Go to Settings → Account → Cancel Subscription</li>
                <li>Your Pro features will remain active until the end of the billing period</li>
                <li>No partial refunds for unused days after the 14-day period</li>
                <li>You can downgrade to the Free plan at any time</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-[#064E3B] mb-3">3. Non-Refundable Items</h3>
              <p className="mb-3">The following are not eligible for refunds:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Renewal payments after the initial 14-day period</li>
                <li>Accounts terminated due to Terms of Service violations</li>
                <li>Promotional or discounted subscriptions (unless specified)</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-[#064E3B] mb-3">4. How to Request a Refund</h3>
              <div className="bg-gray-50 rounded-lg p-6">
                <ol className="list-decimal pl-6 space-y-3">
                  <li>
                    <strong>Email us</strong> at feed@maxefeed.com with the subject line "Refund Request"
                  </li>
                  <li>
                    Include your <strong>registered phone number</strong> and <strong>reason for refund</strong>
                  </li>
                  <li>
                    Our team will review your request within <strong>24-48 hours</strong>
                  </li>
                  <li>
                    Once approved, the refund will be processed to your original payment method within <strong>5-7 business days</strong>
                  </li>
                </ol>
              </div>
            </section>

            <section>
              <div className="flex items-center gap-2 mb-3">
                <HelpCircle className="h-5 w-5 text-[#84CC16]" />
                <h3 className="text-xl font-semibold text-[#064E3B]">5. Questions?</h3>
              </div>
              <p>
                If you have any questions about our refund policy or need assistance, please don't hesitate to contact us:
              </p>
              <div className="mt-3 p-4 bg-gray-50 rounded-lg">
                <p><strong>Email:</strong> feed@maxefeed.com</p>
                <p><strong>Phone:</strong> +1 (832) 688-6863</p>
                <p><strong>Address:</strong> 5900 Balcones Drive 7274, Austin, TX 78731</p>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-[#064E3B] mb-3">6. Frequently Asked Questions</h3>
              <div className="space-y-4">
                <div>
                  <p className="font-semibold text-[#064E3B]">Do I get a refund if I forget to cancel before renewal?</p>
                  <p>Renewal charges after the initial 14-day money-back window are non-refundable, but you can cancel anytime to stop future renewals and keep Pro until the end of your current billing period.</p>
                </div>
                <div>
                  <p className="font-semibold text-[#064E3B]">How long does a refund take to reach me?</p>
                  <p>Once approved, refunds are issued to your original payment method and typically appear within 5–7 business days, depending on your bank or card provider.</p>
                </div>
                <div>
                  <p className="font-semibold text-[#064E3B]">Will I lose my data if I downgrade or get a refund?</p>
                  <p>No. Your transactions, bills, and history stay in your account on the Free plan. You can export everything anytime from Settings → Export Data.</p>
                </div>
                <div>
                  <p className="font-semibold text-[#064E3B]">Is the Free plan really free?</p>
                  <p>Yes. You can track expenses on WhatsApp and use core features on the Free plan with no card required. Refunds only apply to paid Pro subscriptions.</p>
                </div>
              </div>
              <p className="mt-4 text-sm text-gray-500">
                Please also review our{' '}
                <button onClick={() => navigate('/terms')} className="text-[#064E3B] underline font-medium">Terms of Service</button>
                {' '}and{' '}
                <button onClick={() => navigate('/privacy')} className="text-[#064E3B] underline font-medium">Privacy Policy</button>.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Refund;
